import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, Users, Bot, Mail, X, Check, Info, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  listDepartments, companySeats, createDepartment, renameDepartment, deleteDepartment, setDepartmentAgents,
  listDepartmentMembers, addDepartmentMember, removeDepartmentMember, apiError,
} from "@/lib/api";
import { fmtDate } from "@/components/company/CompanyUi";

/**
 * Step 5b: "Departamentos y accesos" (same component for the admin, in the client file, and for the
 * company master, in /mi-empresa). Write actions follow `can_manage` of GET /departments (admin or
 * master); a consultant sees everything read-only. Contracts: STEP5A_INFORME.md §2 + 5b.
 */
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PILL = "inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] whitespace-nowrap";

const KIND = {
  master: ["border-accent-blue/40 bg-accent-blue/5 text-accent-blue", "Master (sin seat)"],
  usuario: ["border-violet-300 bg-violet-50 text-violet-800", "Usuario con login"],
  colaborador: ["border-slate-300 bg-slate-50 text-slate-700", "Colaborador"],
};
const STATUS = {
  activo: ["border-emerald-300 bg-emerald-50 text-emerald-800", "Activo"],
  invitado: ["border-amber-300 bg-amber-50 text-amber-800", "Invitado"],
  sin_departamento: ["border-slate-300 bg-white text-slate-500", "Sin departamento"],
  master: ["border-accent-blue/40 bg-accent-blue/5 text-accent-blue", "Todos los agentes"],
};
const INVITE = {
  sent: ["text-emerald-700", "Invitación enviada"],
  queued: ["text-slate-500", "Invitación en cola de envío"],
  pending_no_smtp: ["text-amber-700", "Invitación registrada; el servidor de correo no está configurado"],
  failed: ["text-red-700", "No se pudo enviar la invitación"],
  no_recipient: ["text-red-700", "Invitación sin destinatario"],
};

const Pill = ({ map, k, testid }) => {
  const [cls, label] = map[k] || ["border-slate-300 text-slate-500", k || "—"];
  return <span className={`${PILL} ${cls}`} data-testid={testid}>{label}</span>;
};

export function SeatsMeter({ seats, testid = "seats-meter" }) {
  if (!seats) return null;
  const plan = Number(seats.plan) || 0;
  const used = Number(seats.used) || 0;
  const pct = plan > 0 ? Math.min(100, Math.round((used / plan) * 100)) : used > 0 ? 100 : 0;
  const full = plan === 0 || used >= plan;
  return (
    <div className="border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">Seats</p>
        <p className="font-mono text-sm text-ink" data-testid={`${testid}-text`}>
          {used} usados de {plan} · {Math.max(0, plan - used)} libres
          {seats.over_plan ? ` · ${used - plan} en exceso` : ""}
        </p>
      </div>
      <div className="mt-2 h-2 w-full bg-slate-100">
        <div className={`h-2 ${seats.over_plan ? "bg-red-500" : full ? "bg-amber-500" : "bg-accent-blue"}`} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Ocupa un seat cada persona distinta (por correo): los usuarios de la empresa con login, salvo el master, y
        los colaboradores agregados a algún departamento. El usuario master no ocupa seat.
      </p>
      {seats.over_plan && (
        <p className="mt-2 text-[12px] text-red-700" data-testid={`${testid}-over`}>
          Hay más personas con acceso que seats en el plan. No se quitó a nadie, pero no se pueden agregar personas
          nuevas hasta liberar seats o ampliar el plan.
        </p>
      )}
    </div>
  );
}

function Confirm({ open, title, children, confirmLabel, onConfirm, onCancel, busy, testid }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && !busy && onCancel()}>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild><div className="space-y-2 text-sm text-slate-600">{children}</div></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`${testid}-cancel`}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={busy}
                             className="bg-red-600 hover:bg-red-700" data-testid={`${testid}-confirm`}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DepartmentCard({ slug, dept, allAgents, canManage, readOnlyReason, members, onChanged, seatsFull }) {
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(dept.name);
  const [editingAgents, setEditingAgents] = useState(false);
  const [picked, setPicked] = useState(dept.agents.map((a) => a.slug));
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type:"dept"} | {type:"member", email}
  const disabledTitle = canManage ? undefined : readOnlyReason;

  useEffect(() => { setName(dept.name); setPicked(dept.agents.map((a) => a.slug)); }, [dept]);

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      const err = apiError(e);
      setError(err);
      if (err.code !== "seats_exhausted") toast.error(err.detail);
    } finally {
      setBusy(null);
    }
  };

  const saveName = () => run("name", async () => {
    const n = name.trim();
    if (!n) throw Object.assign(new Error(), { response: { data: { detail: "Indique el nombre del departamento." } } });
    await renameDepartment(slug, dept.id, n);
    setEditingName(false);
    toast.success("Departamento renombrado.");
    onChanged();
  });

  const saveAgents = () => run("agents", async () => {
    const r = await setDepartmentAgents(slug, dept.id, picked);
    setEditingAgents(false);
    toast.success(r.invites_resent
      ? `Agentes actualizados. Se reenvió la invitación con los agentes nuevos a ${r.invites_resent} persona(s).`
      : "Agentes actualizados.");
    onChanged();
  });

  const addEmail = (e) => {
    e.preventDefault();
    const v = email.trim().toLowerCase();
    if (!EMAIL_RE.test(v)) {
      setError({ code: "invalid_email", detail: "Indique un correo válido, por ejemplo nombre@empresa.com." });
      return;
    }
    run("add", async () => {
      const r = await addDepartmentMember(slug, dept.id, v);
      setEmail("");
      toast.success(r.new_seat ? `Se agregó ${v} (ocupa un seat nuevo) y se le envió la invitación.`
        : `Se agregó ${v} (ya ocupaba un seat) y se le envió la invitación.`);
      onChanged();
    });
  };

  const doDelete = () => run("delete", async () => {
    const r = await deleteDepartment(slug, dept.id);
    setConfirm(null);
    toast.success(`Departamento eliminado. Personas quitadas: ${r.members_removed}.`);
    onChanged();
  });

  const doRemove = (mail) => run(`rm-${mail}`, async () => {
    const r = await removeDepartmentMember(slug, dept.id, mail);
    setConfirm(null);
    toast.success(r.still_has_seat ? `Se quitó ${mail} de este departamento (conserva su seat).`
      : `Se quitó ${mail}; su seat quedó libre.`);
    onChanged();
  });

  const toggle = (s) => setPicked((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  return (
    <article className="border border-slate-200 bg-white" data-testid={`dept-card-${dept.id}`}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
        {editingName ? (
          <div className="flex flex-1 items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoFocus
                   className="h-9 max-w-sm rounded-sm" data-testid={`dept-rename-input-${dept.id}`}
                   onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }} />
            <Button size="sm" onClick={saveName} disabled={busy === "name"} className="rounded-sm" data-testid={`dept-rename-save-${dept.id}`}>
              {busy === "name" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setEditingName(false); setName(dept.name); }} className="rounded-sm"
                    data-testid={`dept-rename-cancel-${dept.id}`}><X className="h-4 w-4" /></Button>
          </div>
        ) : (
          <div className="min-w-0">
            <h3 className="font-display text-xl text-ink" data-testid={`dept-name-${dept.id}`}>{dept.name}</h3>
            <p className="mt-0.5 font-mono text-[11px] text-slate-500">
              {dept.agents.length} agente(s) · {dept.members_count} correo(s)
            </p>
          </div>
        )}
        {!editingName && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={!canManage} title={disabledTitle} onClick={() => setEditingName(true)}
                    className="rounded-sm text-[11px] uppercase tracking-[0.14em]" data-testid={`dept-rename-btn-${dept.id}`}>
              <Pencil className="mr-1 h-3.5 w-3.5" /> Renombrar
            </Button>
            <Button size="sm" variant="outline" disabled={!canManage} title={disabledTitle} onClick={() => setConfirm({ type: "dept" })}
                    className="rounded-sm border-red-300 text-[11px] uppercase tracking-[0.14em] text-red-700 hover:bg-red-50"
                    data-testid={`dept-delete-btn-${dept.id}`}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Eliminar
            </Button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 gap-6 px-5 py-5 lg:grid-cols-2">
        {/* agents */}
        <section data-testid={`dept-agents-${dept.id}`}>
          <div className="flex items-center justify-between gap-2">
            <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600"><Bot className="h-3.5 w-3.5" /> Agentes</p>
            {!editingAgents && (
              <button onClick={() => setEditingAgents(true)} disabled={!canManage} title={disabledTitle}
                      className="text-[11px] uppercase tracking-[0.14em] text-accent-blue hover:underline disabled:cursor-not-allowed disabled:text-slate-400 disabled:no-underline"
                      data-testid={`dept-agents-edit-${dept.id}`}>
                Asignar agentes
              </button>
            )}
          </div>
          {editingAgents ? (
            <div className="mt-3 border border-slate-200 bg-slate-50 p-3" data-testid={`dept-agents-picker-${dept.id}`}>
              {allAgents.length === 0 ? (
                <p className="text-[12px] text-slate-500">La empresa todavía no tiene agentes.</p>
              ) : (
                <ul className="max-h-56 space-y-1.5 overflow-y-auto">
                  {allAgents.map((a) => (
                    <li key={a.slug}>
                      <label className="flex cursor-pointer items-center gap-2 text-[13px] text-slate-800">
                        <input type="checkbox" checked={picked.includes(a.slug)} onChange={() => toggle(a.slug)}
                               className="h-4 w-4 accent-slate-900" data-testid={`dept-agent-check-${dept.id}-${a.slug}`} />
                        {a.name || a.slug}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-[11px] text-slate-500">
                Si agrega agentes nuevos, las personas de este departamento recibirán otra vez la invitación con los enlaces
                de esos agentes.
              </p>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={saveAgents} disabled={busy === "agents"} className="rounded-sm" data-testid={`dept-agents-save-${dept.id}`}>
                  {busy === "agents" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Guardar agentes
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditingAgents(false); setPicked(dept.agents.map((a) => a.slug)); }}
                        className="rounded-sm" data-testid={`dept-agents-cancel-${dept.id}`}>Cancelar</Button>
              </div>
            </div>
          ) : dept.agents.length === 0 ? (
            <p className="mt-3 text-[12px] italic text-slate-500" data-testid={`dept-agents-empty-${dept.id}`}>Sin agentes asignados.</p>
          ) : (
            <ul className="mt-3 flex flex-wrap gap-1.5">
              {dept.agents.map((a) => (
                <li key={a.slug} className="border border-slate-300 bg-slate-50 px-2 py-0.5 text-[12px] text-slate-800" data-testid={`dept-agent-${dept.id}-${a.slug}`}>
                  {a.name || a.slug}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* members */}
        <section data-testid={`dept-members-${dept.id}`}>
          <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600"><Mail className="h-3.5 w-3.5" /> Correos con acceso</p>
          <form onSubmit={addEmail} noValidate className="mt-3 flex gap-2">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nombre@empresa.com"
                   disabled={!canManage} title={disabledTitle} className="h-9 rounded-sm" data-testid={`dept-member-input-${dept.id}`} />
            <Button type="submit" size="sm" disabled={!canManage || busy === "add" || !email.trim()} title={disabledTitle}
                    className="h-9 rounded-sm" data-testid={`dept-member-add-${dept.id}`}>
              {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Agregar
            </Button>
          </form>
          {seatsFull && canManage && !error && (
            <p className="mt-2 text-[11px] text-amber-700" data-testid={`dept-seats-full-hint-${dept.id}`}>
              Todos los seats están en uso: solo puede agregar correos que ya ocupan un seat.
            </p>
          )}
          {error && (
            <div className={`mt-3 flex items-start gap-2 border p-3 text-[12px] ${error.code === "seats_exhausted" ? "border-amber-400 bg-amber-50 text-amber-900" : "border-red-300 bg-red-50 text-red-800"}`}
                 role="alert" data-testid={error.code === "seats_exhausted" ? `dept-seats-exhausted-${dept.id}` : `dept-error-${dept.id}`}>
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error.detail}</span>
            </div>
          )}
          {members === null ? (
            <p className="mt-3 flex items-center gap-2 text-[12px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Cargando…</p>
          ) : members.length === 0 ? (
            <p className="mt-3 text-[12px] italic text-slate-500" data-testid={`dept-members-empty-${dept.id}`}>Sin correos todavía.</p>
          ) : (
            <ul className="mt-3 divide-y divide-slate-100 border border-slate-200">
              {members.map((m) => {
                const [icls, ilabel] = INVITE[m.invite_status] || ["text-slate-400", "Sin registro de invitación"];
                return (
                  <li key={m.email} className="flex items-start justify-between gap-3 px-3 py-2" data-testid={`dept-member-${dept.id}-${m.email}`}>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] text-ink">{m.email}</p>
                      <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px]">
                        <Pill map={STATUS} k={m.status} testid={`dept-member-status-${dept.id}-${m.email}`} />
                        <span className={icls} data-testid={`dept-member-invite-${dept.id}-${m.email}`}>
                          {ilabel}{m.invite_sent_at ? ` · ${fmtDate(m.invite_sent_at)}` : ""}
                        </span>
                        {m.last_access_at && <span className="text-slate-500">Último ingreso: {fmtDate(m.last_access_at)}</span>}
                      </p>
                    </div>
                    <button onClick={() => setConfirm({ type: "member", email: m.email })} disabled={!canManage} title={disabledTitle || "Quitar correo"}
                            className="mt-0.5 text-slate-400 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                            data-testid={`dept-member-remove-${dept.id}-${m.email}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <Confirm open={confirm?.type === "dept"} title={`Eliminar el departamento «${dept.name}»`} confirmLabel="Eliminar departamento"
               busy={busy === "delete"} onCancel={() => setConfirm(null)} onConfirm={doDelete} testid={`dept-delete-dialog-${dept.id}`}>
        <p>Se quitarán sus {dept.members_count} correo(s) y la asignación de sus {dept.agents.length} agente(s). Los agentes no se borran.</p>
        <p>Quien no quede en ningún otro departamento perderá el acceso de inmediato y, si no es usuario de la empresa, su seat quedará libre.</p>
      </Confirm>
      <Confirm open={confirm?.type === "member"} title="Quitar correo del departamento" confirmLabel="Quitar correo"
               busy={busy === `rm-${confirm?.email}`} onCancel={() => setConfirm(null)} onConfirm={() => doRemove(confirm.email)}
               testid={`dept-member-remove-dialog-${dept.id}`}>
        <p><strong>{confirm?.email}</strong> dejará de tener acceso a los agentes de «{dept.name}» en su próxima consulta.</p>
        <p>Si no está en otro departamento ni es usuario de la empresa, su seat quedará libre.</p>
      </Confirm>
    </article>
  );
}

export default function DepartmentsPanel({ slug, readOnlyReason: roReason }) {
  const [data, setData] = useState(null);
  const [seats, setSeats] = useState(null);
  const [members, setMembers] = useState({});
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([listDepartments(slug), companySeats(slug)]);
      setData(d);
      setSeats(s);
      setLoadError(null);
      const entries = await Promise.all(d.departments.map(async (x) => {
        try {
          return [x.id, (await listDepartmentMembers(slug, x.id)).members];
        } catch {
          return [x.id, []];
        }
      }));
      setMembers(Object.fromEntries(entries));
    } catch (e) {
      setLoadError(apiError(e, "No se pudieron cargar los departamentos.").detail);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    const n = newName.trim();
    if (!n) return;
    setCreating(true);
    try {
      await createDepartment(slug, n);
      setNewName("");
      toast.success(`Departamento «${n}» creado.`);
      load();
    } catch (ex) {
      toast.error(apiError(ex, "No se pudo crear el departamento.").detail);
    } finally {
      setCreating(false);
    }
  };

  if (loadError) {
    return <p className="border border-red-300 bg-red-50 p-4 text-sm text-red-800" data-testid="departments-load-error">{loadError}</p>;
  }
  if (!data || !seats) {
    return <div className="flex items-center gap-2 py-10 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando departamentos…</div>;
  }
  const canManage = !!data.can_manage;
  const readOnlyReason = roReason ||
    "Solo el usuario master de la empresa o el administrador de Inviktus pueden modificar departamentos y accesos.";
  const allAgents = [...data.departments.flatMap((d) => d.agents), ...data.agents_without_department]
    .filter((a, i, arr) => arr.findIndex((b) => b.slug === a.slug) === i)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "es"));
  const seatsFull = !seats.plan || seats.used >= seats.plan;

  return (
    <div className="space-y-8" data-testid="departments-panel">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
        <SeatsMeter seats={seats} />
        <div className="border border-slate-200 bg-slate-50 p-4 text-[12px] leading-relaxed text-slate-600" data-testid="departments-help">
          <p className="flex items-center gap-1.5 font-medium text-slate-700"><Info className="h-3.5 w-3.5" /> Cómo funciona</p>
          <p className="mt-1.5">
            Cada departamento agrupa agentes y correos. Las personas agregadas reciben una invitación con los enlaces de los
            agentes y entran con su correo y un código de 6 dígitos, sin contraseña. Los usuarios con login de la empresa
            (salvo el master) solo pueden usar los agentes de los departamentos en los que figura su correo; en Mi empresa
            ven el nombre y el departamento de todos los agentes de la empresa, y en el panel, sus métricas.
          </p>
        </div>
      </div>

      {!canManage && (
        <p className="flex items-start gap-2 border border-slate-300 bg-slate-50 p-3 text-[12px] text-slate-600" data-testid="departments-readonly">
          <Info className="mt-0.5 h-4 w-4 shrink-0" /> {readOnlyReason} Usted tiene acceso de solo lectura.
        </p>
      )}

      <form onSubmit={create} className="flex flex-wrap items-end gap-2" data-testid="dept-create-form">
        <label className="block">
          <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600">Nuevo departamento</span>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={120} placeholder="Por ejemplo: Ventas"
                 disabled={!canManage} title={canManage ? undefined : readOnlyReason} className="mt-1.5 h-9 w-72 rounded-sm" data-testid="dept-create-input" />
        </label>
        <Button type="submit" disabled={!canManage || creating || !newName.trim()} title={canManage ? undefined : readOnlyReason}
                className="h-9 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-slate-800" data-testid="dept-create-btn">
          {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Crear departamento
        </Button>
      </form>

      {data.departments.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-slate-50 p-8 text-center" data-testid="departments-empty">
          <p className="font-display text-xl text-ink">Todavía no hay departamentos</p>
          <p className="mt-1 text-sm text-slate-600">Cree el primero para asignarle agentes y dar acceso a sus colaboradores por correo.</p>
        </div>
      ) : (
        <div className="space-y-5" data-testid="departments-list">
          {data.departments.map((d) => (
            <DepartmentCard key={d.id} slug={slug} dept={d} allAgents={allAgents} canManage={canManage}
                            readOnlyReason={readOnlyReason} members={members[d.id] ?? null} onChanged={load} seatsFull={seatsFull} />
          ))}
        </div>
      )}

      {data.agents_without_department.length > 0 && (
        <p className="text-[12px] text-slate-600" data-testid="agents-without-department">
          Agentes sin departamento (solo pueden usarlos el master y el equipo de Inviktus):{" "}
          {data.agents_without_department.map((a) => a.name || a.slug).join(", ")}.
        </p>
      )}

      <section data-testid="people-list">
        <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-600"><Users className="h-3.5 w-3.5" /> Personas con acceso</p>
        {seats.people.length === 0 ? (
          <p className="mt-3 text-[12px] italic text-slate-500">Nadie todavía.</p>
        ) : (
          <div className="mt-3 overflow-x-auto border border-slate-200">
            <table className="w-full text-left text-[13px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                <tr><th className="px-3 py-2">Correo</th><th className="px-3 py-2">Tipo</th><th className="px-3 py-2">Departamentos</th><th className="px-3 py-2">Estado</th><th className="px-3 py-2">Último ingreso por código</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {seats.people.map((p) => (
                  <tr key={p.email} data-testid={`person-${p.email}`}>
                    <td className="px-3 py-2 text-ink">{p.email}</td>
                    <td className="px-3 py-2"><Pill map={KIND} k={p.kind || (p.is_master ? "master" : "colaborador")} testid={`person-kind-${p.email}`} /></td>
                    <td className="px-3 py-2 text-slate-700">{p.departments.length ? p.departments.map((d) => d.name).join(", ") : (p.kind === "master" ? "Todos los agentes" : "—")}</td>
                    <td className="px-3 py-2"><Pill map={STATUS} k={p.status} testid={`person-status-${p.email}`} /></td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{p.last_access_at ? fmtDate(p.last_access_at) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
